/**
 * 常用司機 /drivers（僅供應方；2026-08-26）
 * 名冊由車隊自己維護，回報訂單司機資訊時可直接選取。
 */
import { useState, type ReactNode } from "react";
import { Plus, Pencil, Trash2, UserRound } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { FormNotice } from "../../components/account/FormNotice";
import { THEAD_ROW } from "../admin/base-data-shared";

type Row = {
  id: number;
  name: string;
  phone: string;
  whatsapp: string | null;
  line: string | null;
  wechat: string | null;
  vehiclePlate: string | null;
};

export default function Drivers() {
  const { t } = useLanguage();
  const listQ = trpc.fleetDriver.list.useQuery();
  const rows = (listQ.data ?? []) as Row[];
  const [editing, setEditing] = useState<Row | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<Row | null>(null);
  const [error, setError] = useState<string | null>(null);

  const removeM = trpc.fleetDriver.remove.useMutation({
    onSuccess: () => {
      setRemoving(null);
      void listQ.refetch();
    },
    onError: (e) => setError(e.message),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "常用司機", "en-US": "My drivers", "zh-CN": "常用司机" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({
              "zh-TW": "維護車隊常用司機；回報訂單司機資訊時可直接選取，不必每次重打。",
              "en-US": "Maintain your regular drivers — pick them when reporting driver info instead of retyping.",
              "zh-CN": "维护车队常用司机；回报订单司机资讯时可直接选取，不必每次重打。",
            })}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" />
          {t({ "zh-TW": "新增司機", "en-US": "Add driver", "zh-CN": "新增司机" })}
        </Button>
      </div>

      {error && <FormNotice kind="error">{error}</FormNotice>}

      {!listQ.isLoading && rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
          <UserRound className="mx-auto mb-2 h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="font-display text-base text-muted-foreground">
            {t({ "zh-TW": "還沒有常用司機", "en-US": "No saved drivers yet", "zh-CN": "还没有常用司机" })}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {t({
              "zh-TW": "回報訂單司機資訊時勾選「加入常用司機資訊」也會出現在這裡。",
              "en-US": "Ticking “save to my drivers” when reporting driver info also adds them here.",
              "zh-CN": "回报订单司机资讯时勾选「加入常用司机资讯」也会出现在这里。",
            })}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-[13px]">
            <thead>
              <tr className={THEAD_ROW}>
                <th className="p-3">{t({ "zh-TW": "司機", "en-US": "Driver", "zh-CN": "司机" })}</th>
                <th className="p-3">{t({ "zh-TW": "車牌", "en-US": "Plate", "zh-CN": "车牌" })}</th>
                <th className="p-3">{t({ "zh-TW": "即時通訊", "en-US": "Messaging", "zh-CN": "即时通讯" })}</th>
                <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Actions", "zh-CN": "操作" })}</th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">{t({ "zh-TW": "載入中…", "en-US": "Loading…", "zh-CN": "载入中…" })}</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                  <td className="p-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="numeric text-[12px] text-muted-foreground">{r.phone}</div>
                  </td>
                  <td className="p-3 font-mono uppercase">{r.vehiclePlate || "—"}</td>
                  <td className="p-3 text-[12px] text-muted-foreground">
                    {[r.whatsapp && `WhatsApp ${r.whatsapp}`, r.line && `LINE ${r.line}`, r.wechat && `WeChat ${r.wechat}`]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(r)} title={t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setError(null); setRemoving(r); }} title={t({ "zh-TW": "刪除", "en-US": "Delete", "zh-CN": "删除" })}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <DriverDialog onClose={() => setCreating(false)} onSaved={() => listQ.refetch()} />}
      {editing && <DriverDialog row={editing} onClose={() => setEditing(null)} onSaved={() => listQ.refetch()} />}

      {removing && (
        <Dialog open onOpenChange={(o) => !o && setRemoving(null)}>
          {/* 純確認、沒有輸入可丟，維持點外面即關閉 */}
          <DialogContent dismissible className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t({ "zh-TW": "刪除司機", "en-US": "Delete driver", "zh-CN": "删除司机" })}</DialogTitle>
              <DialogDescription>
                {t({
                  "zh-TW": `確定要從常用名冊移除「${removing.name}」嗎？已回報過的訂單資訊不受影響。`,
                  "en-US": `Remove “${removing.name}” from your saved drivers? Driver info already reported on orders is unaffected.`,
                  "zh-CN": `确定要从常用名册移除「${removing.name}」吗？已回报过的订单资讯不受影响。`,
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemoving(null)}>{t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}</Button>
              <Button variant="destructive" disabled={removeM.isPending} onClick={() => removeM.mutate({ id: removing.id })}>
                {t({ "zh-TW": "確認刪除", "en-US": "Delete", "zh-CN": "确认删除" })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function DriverDialog({ row, onClose, onSaved }: { row?: Row; onClose: () => void; onSaved: () => void }) {
  const { t } = useLanguage();
  const [f, setF] = useState({
    name: row?.name ?? "",
    phone: row?.phone ?? "",
    vehiclePlate: row?.vehiclePlate ?? "",
    whatsapp: row?.whatsapp ?? "",
    line: row?.line ?? "",
    wechat: row?.wechat ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: string) => setF((s) => ({ ...s, [k]: v }));

  const done = () => { onSaved(); onClose(); };
  const createM = trpc.fleetDriver.create.useMutation({ onSuccess: done, onError: (e) => setError(e.message) });
  const updateM = trpc.fleetDriver.update.useMutation({ onSuccess: done, onError: (e) => setError(e.message) });
  const pending = createM.isPending || updateM.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {row
              ? t({ "zh-TW": "編輯司機", "en-US": "Edit driver", "zh-CN": "编辑司机" })
              : t({ "zh-TW": "新增司機", "en-US": "Add driver", "zh-CN": "新增司机" })}
          </DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "姓名與電話必填，其餘選填。", "en-US": "Name and phone are required; the rest optional.", "zh-CN": "姓名与电话必填，其余选填。" })}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            const payload = {
              name: f.name.trim(),
              phone: f.phone.trim(),
              vehiclePlate: f.vehiclePlate.trim(),
              whatsapp: f.whatsapp.trim(),
              line: f.line.trim(),
              wechat: f.wechat.trim(),
            };
            if (row) updateM.mutate({ id: row.id, ...payload });
            else createM.mutate(payload);
          }}
        >
          <Field label={`${t({ "zh-TW": "司機姓名", "en-US": "Name", "zh-CN": "司机姓名" })} *`}>
            <Input required value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={128} />
          </Field>
          <Field label={`${t({ "zh-TW": "司機電話", "en-US": "Phone", "zh-CN": "司机电话" })} *`}>
            <Input required value={f.phone} onChange={(e) => set("phone", e.target.value)} maxLength={64} />
          </Field>
          <Field label={t({ "zh-TW": "車牌號碼", "en-US": "Vehicle plate", "zh-CN": "车牌号码" })}>
            <Input value={f.vehiclePlate} onChange={(e) => set("vehiclePlate", e.target.value)} maxLength={32} />
          </Field>
          <div className="grid grid-cols-1 gap-2">
            <Field label="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} maxLength={64} /></Field>
            <Field label="LINE"><Input value={f.line} onChange={(e) => set("line", e.target.value)} maxLength={64} /></Field>
            <Field label="WeChat"><Input value={f.wechat} onChange={(e) => set("wechat", e.target.value)} maxLength={64} /></Field>
          </div>
          {error && <FormNotice kind="error">{error}</FormNotice>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}</Button>
            <Button type="submit" disabled={pending || f.name.trim() === "" || f.phone.trim() === ""}>
              {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "储存" })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{label}</Label>
      {children}
    </div>
  );
}
